import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
    Heart, Users, Sparkles, ChevronRight, ArrowRight, 
    Check, MessageCircle, RefreshCw, Shield, HeartHandshake,
    AlertCircle, Zap
} from 'lucide-react';
import CoRegulateLogo from '../components/Logo';
import { SmallText } from '../components/brand/BrandTypography';

// JSON-LD Structured Data for SEO
const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "WebPage",
            "@id": "https://coregulateadhd.com/adhd-relationship-tools",
            "url": "https://coregulateadhd.com/adhd-relationship-tools",
            "name": "ADHD Relationship Tools | CoRegulate",
            "description": "Practical support for ADHD relationships — understand your patterns, break negative cycles, and reconnect with your partner.",
            "isPartOf": { "@id": "https://coregulateadhd.com/#website" },
            "about": { "@id": "https://coregulateadhd.com/#organization" }
        },
        {
            "@type": "Organization",
            "@id": "https://coregulateadhd.com/#organization",
            "name": "CoRegulate",
            "url": "https://coregulateadhd.com",
            "description": "Made for ADHD hearts, shaped by ADHD minds."
        },
        {
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "How does ADHD affect relationships?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "ADHD affects relationships through several patterns: Rejection Sensitivity (perceiving criticism where none was intended), Emotional Flooding (getting overwhelmed during conflict), Time Blindness (forgetting commitments which partners may interpret as not caring), and Shutdown Response (going silent or withdrawing during conflict when overwhelmed)."
                    }
                },
                {
                    "@type": "Question",
                    "name": "What tools help ADHD couples?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Effective tools for ADHD couples include: Break the Cycle (identify and interrupt negative patterns), Relationship Check-in (gentle assessment of relationship patterns), Conflict Crisis Mode (7-step de-escalation for active conflicts), Co-Regulation Tool (guided exercises for calming down together), and Partner Support Guides for non-ADHD partners to understand and support their partner."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How can I support my ADHD partner?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Supporting an ADHD partner involves understanding rejection sensitivity (they may perceive criticism more intensely), learning what helps during shutdown moments, using scripted responses for common situations, and practicing co-regulation techniques together. Remember: the cycle is the problem, not either of you."
                    }
                }
            ]
        },
        {
            "@type": "ItemList",
            "name": "ADHD Relationship Support Tools",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Break the Cycle",
                    "description": "Identify and interrupt negative relationship patterns using EFT-based exercises"
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Relationship Check-in",
                    "description": "Gentle assessment that identifies your relationship patterns and needs"
                },
                {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Conflict Crisis Mode",
                    "description": "7-step de-escalation tool for active conflict situations"
                },
                {
                    "@type": "ListItem",
                    "position": 4,
                    "name": "Co-Regulation Tool",
                    "description": "Guided exercises for calming down together with your partner"
                },
                {
                    "@type": "ListItem",
                    "position": 5,
                    "name": "Solo: What Do I Need?",
                    "description": "Identify your unmet needs when your partner isn't available"
                },
                {
                    "@type": "ListItem",
                    "position": 6,
                    "name": "Partner Support Guide",
                    "description": "Resources for partners of people with ADHD to understand and support"
                }
            ]
        }
    ]
};

const SEOLandingRelationshipTools = () => {
    const navigate = useNavigate();
    
    const tools = [
        {
            name: 'Break the Cycle',
            description: 'Identify and interrupt negative relationship patterns using EFT-based exercises',
            icon: RefreshCw,
            for: 'Couples stuck in repetitive conflicts'
        },
        {
            name: 'Relationship Check-in',
            description: 'Gentle assessment that identifies your relationship patterns and needs',
            icon: MessageCircle,
            for: 'Understanding your dynamic'
        },
        {
            name: 'Conflict Crisis Mode',
            description: '7-step de-escalation tool for active conflict situations',
            icon: AlertCircle,
            for: 'In-the-moment support'
        },
        {
            name: 'Co-Regulation Tool',
            description: 'Guided exercises for calming down together with your partner',
            icon: HeartHandshake,
            for: 'Reconnecting after conflict'
        },
        {
            name: 'Solo: What Do I Need?',
            description: 'Identify your unmet needs when your partner isn\'t available',
            icon: Heart,
            for: 'Self-reflection'
        },
        {
            name: 'Partner Support Guide',
            description: 'Resources for partners of people with ADHD to understand and support',
            icon: Users,
            for: 'Non-ADHD partners'
        }
    ];
    
    const adhdPatterns = [
        {
            pattern: 'Rejection Sensitivity',
            description: 'Perceiving criticism or rejection where none was intended, leading to defensive reactions'
        },
        {
            pattern: 'Emotional Flooding',
            description: 'Getting overwhelmed by emotions during conflict, making communication impossible'
        },
        {
            pattern: 'Time Blindness',
            description: 'Forgetting commitments or running late, which partners may interpret as not caring'
        },
        {
            pattern: 'Shutdown Response',
            description: 'Going silent or withdrawing during conflict when overwhelmed'
        }
    ];

    return (
        <div className="min-h-screen bg-white/60">
            {/* SEO Meta Tags and Structured Data */}
            <Helmet>
                <title>ADHD Relationship Tools | CoRegulate</title>
                <meta name="description" content="Practical support for ADHD relationships — understand your patterns, break negative cycles, and reconnect with your partner." />
                <meta name="keywords" content="ADHD relationship problems, ADHD couples tools, ADHD relationship support, ADHD partner, rejection sensitivity" />
                <link rel="canonical" href="https://coregulateadhd.com/adhd-relationship-tools" />
                <meta property="og:title" content="ADHD Relationship Tools | CoRegulate" />
                <meta property="og:description" content="Practical support for ADHD relationships — understand your patterns, break negative cycles, and reconnect with your partner." />
                <meta property="og:url" content="https://coregulateadhd.com/adhd-relationship-tools" />
                <meta property="og:type" content="website" />
            </Helmet>
            
            {/* JSON-LD Structured Data */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            
            {/* Header */}
            <header className="sticky top-0 z-50 bg-white/60/95 backdrop-blur-sm border-b border-white/40/30">
                <nav className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/landing" className="flex items-center gap-2">
                        <CoRegulateLogo size="md" />
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link 
                            to="/auth?mode=login"
                            className="hidden sm:flex px-4 py-2 text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                        >
                            Sign in
                        </Link>
                        <Link 
                            to="/initial-assessment"
                            className="px-5 py-2.5 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white text-sm font-medium rounded-full hover:shadow-lg transition-all"
                            data-testid="nav-cta"
                        >
                            Try Free
                        </Link>
                    </div>
                </nav>
            </header>
            
            {/* Hero Section */}
            <section className="py-16 md:py-24 overflow-hidden">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFBB98]/20 rounded-full mb-6">
                        <Heart className="w-4 h-4 text-[#E08850]" />
                        <SmallText className="text-[#E08850] font-medium">ADHD & Relationships</SmallText>
                    </div>
                    
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1D6B6B] mb-6" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        ADHD Relationship Tools
                    </h1>
                    
                    <p className="text-lg md:text-xl text-[#7D8E95] max-w-2xl mx-auto mb-8">
                        Practical support for ADHD relationships — understand your patterns, break negative cycles, and reconnect with your partner.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button 
                            onClick={() => navigate('/initial-assessment')}
                            className="group px-8 py-4 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white font-medium rounded-full hover:shadow-lg transition-all flex items-center justify-center gap-2"
                            data-testid="hero-cta"
                        >
                            Start Your Check-in
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button 
                            onClick={() => navigate('/adhd-emotional-regulation')}
                            className="px-8 py-4 bg-white/70 text-[#1D6B6B] font-medium rounded-full border border-[#7D8E95]/30 hover:bg-white transition-all"
                        >
                            Regulation Tools
                        </button>
                    </div>
                </div>
            </section>
            
            {/* ADHD in Relationships */}
            <section className="py-16 bg-white/70">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            How ADHD Affects Relationships
                        </h2>
                        <p className="text-[#7D8E95] max-w-2xl mx-auto">
                            ADHD doesn't just affect focus — it impacts how we connect, communicate, and handle conflict.
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                        {adhdPatterns.map((item, index) => (
                            <div 
                                key={item.pattern}
                                className="bg-white/60 rounded-2xl p-6 border border-white/40/20"
                            >
                                <h3 className="font-medium text-[#1D6B6B] mb-2">{item.pattern}</h3>
                                <p className="text-sm text-[#7D8E95]">{item.description}</p>
                            </div>
                        ))}
                    </div>
                    
                    <div className="mt-8 p-6 bg-[#1D6B6B]/5 rounded-2xl border border-[#1D6B6B]/10 text-center">
                        <p className="text-[#1D6B6B] font-medium mb-2">
                            "The cycle is the problem — not either of you."
                        </p>
                        <p className="text-sm text-[#7D8E95]">
                            Understanding these patterns is the first step to breaking them.
                        </p>
                    </div>
                </div>
            </section>
            
            {/* Tools Grid */}
            <section className="py-16">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Relationship Support Tools
                        </h2>
                        <p className="text-[#7D8E95]">
                            Whether you're in conflict or wanting to connect, we have tools for every moment
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tools.map((tool, index) => (
                            <div 
                                key={tool.name}
                                className="bg-white/60 rounded-2xl p-6 border border-white/40/20 hover:shadow-lg hover:-translate-y-1 transition-all"
                            >
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFBB98]/20 to-[#FFBB98]/30 flex items-center justify-center mb-4">
                                    <tool.icon className="w-6 h-6 text-[#E08850]" />
                                </div>
                                <h3 className="font-medium text-[#1D6B6B] mb-2">{tool.name}</h3>
                                <p className="text-sm text-[#7D8E95] mb-3">{tool.description}</p>
                                <div className="text-xs text-[#1D6B6B] font-medium">
                                    For: {tool.for}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* For Partners Section */}
            <section className="py-16 bg-white/70">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-8 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#7D8E95]/10 rounded-full mb-4">
                                <Users className="w-4 h-4 text-[#7D8E95]" />
                                <span className="text-sm text-[#7D8E95] font-medium">For Partners</span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                                Supporting Your ADHD Partner
                            </h2>
                            <p className="text-[#7D8E95] mb-6">
                                If you love someone with ADHD, you know it comes with unique challenges. 
                                Our Partner Support Guide helps you understand what's happening and how to respond.
                            </p>
                            <ul className="space-y-3">
                                <li className="flex items-start gap-2">
                                    <Check className="w-5 h-5 text-[#1D6B6B] mt-0.5" />
                                    <span className="text-[#3A3A37]">Understand rejection sensitivity</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="w-5 h-5 text-[#1D6B6B] mt-0.5" />
                                    <span className="text-[#3A3A37]">Learn what helps during shutdown</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Check className="w-5 h-5 text-[#1D6B6B] mt-0.5" />
                                    <span className="text-[#3A3A37]">Get scripted responses for common situations</span>
                                </li>
                            </ul>
                        </div>
                        <div className="bg-white/60 rounded-2xl p-8 border border-white/40/20">
                            <p className="text-[#7D8E95] italic mb-4">
                                "I finally understand why my partner shuts down. These tools helped us stop blaming each other and start working as a team."
                            </p>
                            <p className="text-sm text-[#1D6B6B] font-medium">— Partner of someone with ADHD</p>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-br from-[#1D6B6B] to-[#145555]">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <HeartHandshake className="w-12 h-12 text-white/80 mx-auto mb-6" />
                    <h2 className="text-3xl font-semibold text-white mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        Start Reconnecting Today
                    </h2>
                    <p className="text-white/80 mb-8 max-w-xl mx-auto">
                        Take a gentle relationship check-in to understand your patterns and get personalized tool recommendations.
                    </p>
                    <button 
                        onClick={() => navigate('/initial-assessment')}
                        className="group px-10 py-4 bg-white text-[#1D6B6B] font-semibold rounded-full hover:bg-white/60 hover:shadow-lg transition-all flex items-center justify-center gap-2 mx-auto"
                        data-testid="final-cta"
                    >
                        Take the Relationship Check-in
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </section>
            
            {/* Footer */}
            <footer className="bg-[#145555] text-white py-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <CoRegulateLogo size="small" variant="light" />
                    <p className="text-white/60 text-sm mt-4 mb-6">
                        Made for ADHD hearts, shaped by ADHD minds.
                    </p>
                    <div className="flex justify-center gap-6 text-sm text-white/60">
                        <Link to="/landing" className="hover:text-white transition-colors">Home</Link>
                        <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                        <Link to="/adhd-emotional-regulation" className="hover:text-white transition-colors">Regulation Tools</Link>
                        <Link to="/crisis" className="hover:text-white transition-colors">Crisis Resources</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default SEOLandingRelationshipTools;
